import { Capacitor } from "@capacitor/core";
import type { PluginListenerHandle } from "@capacitor/core";

/**
 * A fake `@capgo/inappbrowser`, enabled by `REACT_APP_MOCK_IN_APP_BROWSER=true`.
 *
 * The in-app assignment import (REFSIX-568) is otherwise unobservable outside a
 * native build: the plugin opens a real WebView, the user signs in to a real
 * platform inside it, and the app's only view of any of that is three plugin
 * listeners. There is no way to drive that sequence from a browser, so the part
 * most likely to regress — the WAITING disposition, which every page before the
 * schedule takes — had no coverage at all.
 *
 * With this installed the whole message sequence is drivable from the console
 * (`pnpm run phone:local`) and from Cypress. Mirrors the existing MockWatch
 * (`REACT_APP_MOCK_WATCH_SERVICE`) and mockUXCam (`REACT_APP_MOCK_UXCAM`)
 * patterns: a local stand-in for something we cannot drive in tests.
 *
 * Unlike the UXCam mock this cannot be registered at `Capacitor.Plugins` —
 * `registerPlugin` overwrites that entry with its own proxy — so
 * `inAppAssignmentImport` looks for `window.__inAppBrowserMock` instead.
 */

type Listener = (event: unknown) => void;

/** The two globals the mock deals in: Cypress's marker, and the mock itself. */
interface MockWindow extends Window {
    Cypress?: unknown;
    __inAppBrowserMock?: MockInAppBrowserApi;
}

const mockWindow = window as MockWindow;

export interface MockInAppBrowserApi {
    /** Every plugin call this page load, in order. */
    calls: { method: string; args?: unknown }[];
    /** Whether the WebView is notionally open. */
    open: boolean;
    /** The URL it is currently showing. */
    url?: string;
    /** Every URL it has been navigated to, in order. */
    visited: string[];
    /** Scripts handed to executeScript, in order. */
    injected: string[];
    /** Event names currently listened for — a leak shows up as leftovers here. */
    listening: () => string[];

    /** Pretend a page finished loading, which is what triggers injection. */
    loadPage: () => void;
    /** Post a message as the injected scraper would. */
    postMessage: (detail: unknown) => void;
    /** Pretend the user dismissed the WebView. */
    dismiss: () => void;
    reset: () => void;
}

export function createMockInAppBrowser(): MockInAppBrowserApi &
    Record<string, unknown> {
    const listeners = new Map<string, Set<Listener>>();
    const state = {
        calls: [] as { method: string; args?: unknown }[],
        open: false,
        url: undefined as string | undefined,
        visited: [] as string[],
        injected: [] as string[],
    };

    const record = (method: string, args?: unknown) => {
        state.calls.push(args === undefined ? { method } : { method, args });
    };

    const emit = (eventName: string, event: unknown) => {
        // Copied before iterating: a listener that resolves the import removes
        // all three, which would otherwise mutate the set mid-iteration.
        Array.from(listeners.get(eventName) ?? []).forEach((listener) => {
            try {
                listener(event);
            } catch (err) {
                console.warn(
                    `[mock InAppBrowser] ${eventName} listener threw`,
                    err
                );
            }
        });
    };

    const mock = {
        get calls() {
            return state.calls;
        },
        get open() {
            return state.open;
        },
        get url() {
            return state.url;
        },
        get visited() {
            return state.visited;
        },
        get injected() {
            return state.injected;
        },

        listening: () =>
            Array.from(listeners.entries())
                .filter(([, set]) => set.size > 0)
                .map(([eventName]) => eventName),

        // --- the plugin surface inAppAssignmentImport uses ---

        addListener: async (
            eventName: string,
            listener: Listener
        ): Promise<PluginListenerHandle> => {
            record("addListener", eventName);
            if (!listeners.has(eventName)) {
                listeners.set(eventName, new Set());
            }
            listeners.get(eventName)!.add(listener);
            return {
                remove: async () => {
                    record("removeListener", eventName);
                    listeners.get(eventName)?.delete(listener);
                },
            };
        },

        openWebView: async (options: { url: string }) => {
            record("openWebView", options);
            state.open = true;
            state.url = options?.url;
            state.visited.push(options?.url);
        },

        setUrl: async (options: { url: string }) => {
            record("setUrl", options);
            state.url = options?.url;
            state.visited.push(options?.url);
        },

        executeScript: async (options: { code: string }) => {
            record("executeScript");
            state.injected.push(options?.code ?? "");
        },

        close: async () => {
            record("close");
            state.open = false;
        },

        // --- the driving surface a test or the console uses ---

        loadPage: () => {
            emit("browserPageLoaded", {});
        },

        postMessage: (detail: unknown) => {
            emit("messageFromWebview", { detail });
        },

        dismiss: () => {
            state.open = false;
            emit("closeEvent", {});
        },

        reset: () => {
            listeners.clear();
            state.calls = [];
            state.open = false;
            state.url = undefined;
            state.visited = [];
            state.injected = [];
        },
    };

    return mock as unknown as MockInAppBrowserApi & Record<string, unknown>;
}

/**
 * The query parameter / localStorage key that turns the stand-in on by hand.
 */
const MOCK_OPT_IN_KEY = "mockInAppBrowser";

/**
 * Whether the stand-in has been asked for on this browser.
 *
 * Cypress always gets it. Everyone else has to opt in — `?mockInAppBrowser=1`
 * once, which sticks until `?mockInAppBrowser=0` — because the env flag is set
 * for whole environments (`local`, `tim`, `anup`, `ci-pipeline-tests`), and on
 * its own it made every desktop browser in those envs offer an Import button
 * that opens a fake WebView and does nothing. A developer looking at the page
 * should see what a referee on a desktop sees.
 */
function mockOptedIn(): boolean {
    if (mockWindow.Cypress) return true;

    try {
        const asked = new URLSearchParams(window.location.search).get(
            MOCK_OPT_IN_KEY
        );
        if (asked !== null) {
            if (asked === "0" || asked === "false") {
                window.localStorage.removeItem(MOCK_OPT_IN_KEY);
            } else {
                window.localStorage.setItem(MOCK_OPT_IN_KEY, "true");
            }
        }
        return window.localStorage.getItem(MOCK_OPT_IN_KEY) === "true";
    } catch (err) {
        console.warn("[mock InAppBrowser] could not read the opt-in", err);
        return false;
    }
}

/**
 * Whether this build stands in for the InAppBrowser rather than using the real
 * plugin.
 *
 * Deliberately the env flag and not `!!window.__inAppBrowserMock`: the platform
 * page reads this while deciding what to render, and the mock is installed
 * during app init — so an "is it there yet" check is a render-order race, and
 * React would not re-render when the answer changed. The flag gives the same
 * answer before and after installation.
 *
 * This exists because the in-app import is gated on `isIos() || isAndroid()`,
 * and that predicate cannot be faked globally: `OneSignalInit` reads the same
 * one at module scope, so convincing Ionic the app is native switches on the
 * native push path and its Cordova plugin is not there in a browser.
 */
export function inAppBrowserIsMocked(): boolean {
    return (
        process.env.REACT_APP_MOCK_IN_APP_BROWSER === "true" &&
        // The flag is set for envs that also produce native builds, where the
        // real plugin must always win.
        !Capacitor.isNativePlatform() &&
        mockOptedIn()
    );
}

/**
 * Expose the mock on `window.__inAppBrowserMock` for the console and Cypress.
 * No-op unless the build sets `REACT_APP_MOCK_IN_APP_BROWSER=true` *and* this
 * browser opted in (`?mockInAppBrowser=1`, or Cypress), and never on a device.
 *
 * Idempotent, and safe to call late: `inAppAssignmentImport` calls it on the
 * way to its first plugin call, so the mock is in place however app init is
 * ordered.
 */
export function installMockInAppBrowser(): MockInAppBrowserApi | undefined {
    if (!inAppBrowserIsMocked()) return undefined;

    const existing = mockWindow.__inAppBrowserMock;
    if (existing) return existing;

    const mock = createMockInAppBrowser();
    mockWindow.__inAppBrowserMock = mock;
    // Loud on purpose: the app is running against a faked plugin, and this names
    // the handles for driving it.
    console.warn(
        "[mock InAppBrowser] installed — drive an import with " +
            "window.__inAppBrowserMock.loadPage() / .postMessage(detail) / .dismiss()"
    );
    return mock;
}
